import { Link } from 'react-router-dom';
import { MapPin, Mail } from 'lucide-react';

const FB_URL = 'https://www.facebook.com/profile.php?id=61577412006699';
const IG_URL = 'https://www.instagram.com/rantmitrahub/';

function FacebookIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
    </svg>
  );
}

function InstagramIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/logo-icon.png" alt="RM" className="h-10 w-auto object-contain" />
              <div>
                <div className="font-bold text-lg leading-tight" style={{fontFamily:'Outfit,sans-serif'}}>
                  <span className="text-white">RENT </span><span className="text-orange-400">MITRA</span>
                </div>
                <div className="text-xs text-slate-400">Your Complete Rental Marketplace</div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              India's most trusted platform to find, list, and manage rental properties. Connecting tenants with the perfect homes since 2024.
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              <div className="flex items-center gap-1.5 text-sm text-slate-400">
                <MapPin className="w-3.5 h-3.5 text-blue-400" />
                Pan India
              </div>
              <div className="flex items-center gap-1.5 text-sm text-slate-400">
                <Mail className="w-3.5 h-3.5 text-blue-400" />
                info@rentmitrahub.com
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-5">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3">Follow Us</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={FB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1877F2] hover:bg-[#166fe5] text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-blue-900/30 active:scale-95"
                  data-testid="footer-facebook-link"
                  aria-label="Follow Rent Mitra on Facebook"
                >
                  <FacebookIcon className="w-4 h-4" />
                  Facebook
                </a>
                <a
                  href={IG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] hover:opacity-90 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg active:scale-95"
                  data-testid="footer-instagram-link"
                  aria-label="Follow Rent Mitra on Instagram"
                >
                  <InstagramIcon className="w-4 h-4" />
                  Instagram
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-2.5">
              {[
                { to: '/search', label: 'Search Properties' },
                { to: '/plans', label: 'Pricing Plans' },
                { to: '/add-property', label: 'List Your Property' },
                { to: '/dashboard', label: 'My Dashboard' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-slate-400 hover:text-white text-sm transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Property Types */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Property Types</h3>
            <ul className="space-y-2.5">
              {['Apartments', 'Villas', 'PG / Hostels', 'Office Spaces', 'Shops', 'Industrial'].map(type => (
                <li key={type}>
                  <Link to={`/search?property_type=${type.toLowerCase()}`} className="text-slate-400 hover:text-white text-sm transition-colors">{type}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-slate-500 text-sm">
            © 2025 Rent Mitra. All rights reserved. &nbsp;·&nbsp;
            <a href="/privacy-policy" className="hover:text-slate-300 transition-colors underline-offset-2 hover:underline">Privacy Policy</a>
          </p>
          <div className="flex items-center gap-4">
            <a
              href={FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-[#1877F2] transition-colors"
              aria-label="Facebook"
              data-testid="footer-facebook-icon"
            >
              <FacebookIcon className="w-5 h-5" />
            </a>
            <a
              href={IG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-[#e6683c] transition-colors"
              aria-label="Instagram"
              data-testid="footer-instagram-icon"
            >
              <InstagramIcon className="w-5 h-5" />
            </a>
            <p className="text-slate-500 text-sm">Made with care for Indian renters</p>
          </div>
        </div>
      </div>

      {/* Floating social buttons on mobile */}
      <div className="fixed bottom-5 left-4 z-40 md:hidden flex flex-col gap-2">
        <a
          href={FB_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#1877F2] hover:bg-[#166fe5] text-white rounded-full text-sm font-semibold shadow-lg shadow-blue-900/40 transition-all active:scale-95"
          data-testid="floating-facebook-btn"
          aria-label="Follow on Facebook"
        >
          <FacebookIcon className="w-4 h-4" />
          Facebook
        </a>
        <a
          href={IG_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] hover:opacity-90 text-white rounded-full text-sm font-semibold shadow-lg shadow-pink-900/30 transition-all active:scale-95"
          data-testid="floating-instagram-btn"
          aria-label="Follow on Instagram"
        >
          <InstagramIcon className="w-4 h-4" />
          Instagram
        </a>
      </div>
    </footer>
  );
}
